import { SettingsTranslationType } from "../../features/settings.types";

export const settings: SettingsTranslationType = {
  settings: {
    title: "Settings",
    aiProvider: {
      label: "AI Provider",
      select: "Select provider",
      description: "Choose which AI provider to use for story generation",
      updateSuccess: "AI Provider Updated",
      updateSuccessDesc: "Your AI provider preference has been saved",
      updateError: "Update Failed",
      updateErrorDesc: "Failed to update AI provider. Please try again."
    },
    language: {
      label: "Language",
      description: "Choose your preferred interface language"
    },
    subscription: {
      title: "Subscription",
      currentPlan: "Current Plan",
      freeTier: "Free",
      basicTier: "Basic",
      premiumTier: "Premium",
      status: "Status",
      renewalDate: "Renewal Date",
      upgradePlan: "Upgrade Plan",
      managePlan: "Manage Plan",
      cancelPlan: "Cancel Plan",
      features: "Features",
      billingCycle: "Billing Cycle",
      monthlyLabel: "Monthly",
      yearlyLabel: "Yearly",
      priceUnits: {
        month: "month",
        year: "year"
      },
      tiers: {
        free: {
          name: "Free",
          description: "Basic features to get started",
          price: "Free"
        },
        basic: {
          name: "Basic",
          description: "Advanced features for hobby writers",
          price: {
            monthly: "$9.99/month",
            yearly: "$99/year"
          }
        },
        premium: {
          name: "Premium",
          description: "Full access for professional writers",
          price: {
            monthly: "$19.99/month",
            yearly: "$199/year"
          }
        }
      },
      browseCharacters: "Browse Characters",
      upgradeToBrowse: "Upgrade to browse your collection of characters",
      upgradeToMakePublic: "Upgrade to make characters public",
      upgradeRequired: "Upgrade Required",
      upgradeToBrowseDescription: "Upgrade your account to browse and reuse your collection of characters.",
      upgradeToMakePublicDescription: "Upgrade your account to share characters publicly with other users.",
      unlockFeatures: "Unlock premium features with a subscription",
      upgradeNow: "Upgrade Now",
      unlockAiTitleGeneration: "Unlock AI Title Generation",
      upgradeForCharaterAccess: "Upgrade to access character management",
      upgradeForStatsAccess: "Upgrade to access stats and badges",
      upgradeForAccess: "Upgrade to access this feature",
      upgradeForAiTitleDescription: "Upgrade to Basic plan to access AI-powered title generation and get creative suggestions based on your story's themes, characters, and settings.",
      generateTitlesFeature: "Generate unique titles instantly",
      aiTitleGenerationPremium: "AI Title Generation is available in Basic plan",
      unlockGenre: "Upgrade to unlock this genre",
      unlockCustomGenres: "Unlock Custom Genres",
      upgradeForCustomGenresDescription: "Upgrade to Basic plan to create custom genres and make your stories more unique.",
      addCustomGenresFeature: "Add unlimited custom genres",
      unlockEnvironment: "Upgrade to unlock this environment",
      upgradeForCustomEnvironments: "Upgrade to TaleCraft Basic to add custom environments",
      unlockCustomEnvironments: "Unlock Custom Environments",
      upgradeForCustomEnvironmentsDescription: "Upgrade to Basic plan to create custom environments and make your stories more unique.",
      addCustomEnvironmentsFeature: "Add unlimited custom environments",
      unlockTheme: "Upgrade to unlock this theme",
      upgradeForCustomThemes: "Upgrade to TaleCraft Basic to add custom themes",
      unlockCustomThemes: "Unlock Custom Themes",
      upgradeForCustomThemesDescription: "Upgrade to Basic plan to create custom themes and make your stories more unique.",
      addCustomThemesFeature: "Add unlimited custom themes",
      unlockStoryLength: "Upgrade to unlock this story length",
      upgradeForPremiumLengths: "Upgrade to TaleCraft Basic to access longer stories",
      unlockPremiumLengths: "Unlock Premium Story Lengths",
      upgradeForPremiumLengthsDescription: "Upgrade to Premium plan to create longer stories and develop your narratives in greater detail.",
      longStoriesFeature: "Create stories with no length restrictions",
      avatarGenerator: {
        upgradeRequired: "Upgrade Required",
        upgradeDescription: "Upgrade your account to generate character avatars.",
        premiumFeature: "Generate custom character avatars with a premium subscription",
        upgradeNow: "Upgrade Now",
        upgradeTooltip: "Upgrade to generate character avatars"
      },
      planComparison: {
        chooseYourPlan: "Choose the plan that best fits your needs",
        price: "Price",
        features: "Features",
        talecraftBasic: "TaleCraft Basic",
        talecraftPremium: "TaleCraft Premium",
        savePercentage: "Save 17%",
        currentPlanLabel: "Current Plan:",
        processing: "Processing...",
        downgradeToBasic: "Downgrade to TaleCraft Basic",
        downgradeToFree: "Downgrade to Free Plan",
        upgradeToBasic: "Upgrade to TaleCraft Basic",
        upgradeToPremium: "Upgrade to TaleCraft Premium",
        downgradingTo: "Downgrading to",
        effectiveOn: "Effective on",
        nextBillingDate: "Next Billing Date",
        featureItems: {
          dailyStoryLimit: "Daily Story Limit",
          aiStoryGeneration: "AI Story generation",
          customResponses: "Custom responses",
          aiTitleSuggestion: "AI Title Suggestion",
          customStorySelection: "Custom Story Selection",
          publicSharing: "Public Sharing",
          characterManagement: "Character Management",
          aiProviderSelection: "AI Provider Selection",
          statsAndBadges: "Stats & Badge/Ranking",
          badgeRewards: "Badge rewards",
          naturalStoryLength: "Natural story length",
          sequelsAndExtract: "Sequels & Extract Characters",
          characterAvatar: "Character Avatar",
          earlyAccessFeatures: "Early access features"
        },
        limits: {
          perDay: "/day",
          unlimited: "Unlimited"
        }
      }
    },
    profile: {
      displayName: {
        label: "Display Name",
        placeholder: "Enter your display name",
        updateButton: "Update Name",
        helpText: "Choose a unique display name for your profile"
      },
      inviteFriends: {
        label: "Invite Friends",
        button: "Invite Friends",
        dialogTitle: "Share Invitation",
        dialogDescription: "Invite your friends to join you on your storytelling journey. Share your unique invitation link or use social media.",
        copySuccess: {
          title: "Link copied!",
          description: "The referral link has been copied to your clipboard."
        },
        copyFailure: {
          title: "Failed to copy",
          description: "Please try copying the link manually."
        },
        shareMessage: "Join me on our storytelling adventure! Create and share amazing stories together.",
        socialPlatforms: {
          facebook: "Facebook",
          X: "X",
          linkedin: "LinkedIn",
        }
      },
      upgradeDialog: {
        title: "Upgrade Required",
        aiProviderDescription: "Upgrade to select an AI provider.",
        unlockProviderSelection: "Unlock AI provider selection with a subscription"
      }
    },    
    issues: {
      title: "Submit an issue",
      description: "Found a bug or have a feature request? Submit it here!",
      submitSuccess: "Issue submitted successfully!",
      submitSuccessDesc: "Thank you for your contribution.",
      submitError: "Error submitting issue",
      submitErrorDesc: "Please try again later.",
      form: {
        titleLabel: "Title",
        titlePlaceholder: "Enter a title",
        descriptionLabel: "Description",
        descriptionPlaceholder: "Enter a description",
        priorityLabel: "Priority",
        priorityPlaceholder: "Select a priority",
        priorityLow: "Low",
        priorityMedium: "Medium",
        priorityHigh: "High",
        priorityDescription: "How important is this issue?",
        submit: "Submit",
        submitting: "Submitting...",
        imagesLabel: "Attach Images (Optional)",
        typeLabel: "Type of issue",
        typePlaceholder: "Select a type of issue",
        typeBug: "Bug",
        typeRequest: "Request",
        typeDescription: "Select the type of issue you are submitting",
      },
      imageUpload: {
        dragDrop: "Drag & drop images here",
        dropHere: "Drop images here...",
        clickToSelect: "or click to select files",
        maxSize: "Max 5MB per file",
        maxNumber: "Max 5 images",
        attachedImages: "Attached Images",
        invalidFiles: "Some files were not accepted",
        sizeTypeLimit: "Files must be images under 5MB",
        tooManyImages: "Too many images",
        limitReached: "You can only attach up to 5 images",
        pasteSupported: "Paste images supported",
        pasteSuccess: "Image pasted",
        pasteSuccessDesc: "Image from clipboard has been attached",
        pasteFailed: "Failed to paste image",
        pasteFailedDesc: "Could not process the clipboard content",
        processing: "Processing image...",
      },
    },
  }
};
